import React, { useEffect, useRef, useState } from 'react';
import './SideMenu.css';

// Left side menu (2026-10-16): every management screen that used to be a horizontal tab lives
// here, grouped, so the chat screen stays clean. It only navigates - it never duplicates the
// business logic of a screen. Chat actions are sent to the chat as commands (see App.jsx).

const svg = (path, extra = null) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={path} />{extra}
  </svg>
);

export const MENU_ICONS = {
  coins: svg('M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3zM4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6'),
  chat: svg('M21 12c0 4.4-4 8-9 8a9.9 9.9 0 01-4.3-1L3 20l1.4-3.7A7.4 7.4 0 013 12c0-4.4 4-8 9-8s9 3.6 9 8z'),
  plus: svg('M12 8v8M8 12h8M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
  list: svg('M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01'),
  trash: svg('M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12M9 7V4h6v3'),
  folder: svg('M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z'),
  phone: svg('M8 2h8a2 2 0 012 2v16a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2zM11 18h2'),
  rocket: svg('M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2M14 4c3-1 6-1 6-1s0 3-1 6l-6 6-5-5 6-6zM9 10H5l-2 2 4 1M14 15v4l-2 2-1-4'),
  link: svg('M13.8 10.2a4 4 0 010 5.6l-3 3a4 4 0 01-5.6-5.6l1.5-1.5M10.2 13.8a4 4 0 010-5.6l3-3a4 4 0 015.6 5.6l-1.5 1.5'),
  globe: svg('M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 010 18M12 3a14 14 0 000 18'),
  tools: svg('M14.7 6.3a4 4 0 00-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 005.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5z'),
  chart: svg('M4 20V10M10 20V4M16 20v-7M22 20H2'),
  bolt: svg('M13 10V3L4 14h7v7l9-11h-7z'),
  user: svg('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
  language: svg('M3 5h12M9 3v2M5 9c1.5 4 4 6 8 7M13 5c-1 5-4 9-9 11M14 21l4-9 4 9M15.5 18h5'),
  logs: svg('M8 9l3 3-3 3M13 15h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'),
  help: svg('M18.4 5.6l-3.5 3.5M14.9 14.9l3.5 3.5M9.1 9.1L5.6 5.6M9.1 14.9l-3.5 3.5M21 12a9 9 0 11-18 0 9 9 0 0118 0zM16 12a4 4 0 11-8 0 4 4 0 018 0z'),
  share: svg('M8.7 13.3l6.6 3.4M15.3 7.3l-6.6 3.4M21 5a3 3 0 11-6 0 3 3 0 016 0zM9 12a3 3 0 11-6 0 3 3 0 016 0zM21 19a3 3 0 11-6 0 3 3 0 016 0z'),
  back: svg('M10 19l-7-7 7-7M3 12h18'),
  logout: svg('M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l-5-5 5-5M5 12h12'),
  chevron: svg('M9 6l6 6-6 6'),
  close: svg('M6 6l12 12M18 6L6 18')
};

/** The menu structure. `tab` navigates to an existing screen; `command` is sent to the chat. */
export function menuGroups(t) {
  return [
    {
      id: 'chats', title: t('menu.groupChats'), icon: 'chat',
      items: [
        { id: 'new-chat', command: 'new', icon: 'plus', tone: 'blue', label: t('menu.newChat'), hint: t('menu.newChatHint') },
        { id: 'all-chats', command: 'history', icon: 'list', tone: 'slate', label: t('menu.allChats'), hint: t('menu.allChatsHint') },
        { id: 'delete-chat', command: 'delete', icon: 'trash', tone: 'red', label: t('menu.deleteChat'), hint: t('menu.deleteChatHint') }
      ]
    },
    {
      id: 'workspace', title: t('menu.groupWorkspace'),
      items: [
        { id: 'projects', tab: 'projects', icon: 'folder', tone: 'cyan', label: t('menu.projects'), hint: t('menu.projectsHint') },
        { id: 'my-apps', command: 'myApp', icon: 'phone', tone: 'purple', label: t('menu.myApps'), hint: t('menu.myAppsHint') },
        { id: 'publishing', tab: 'publishing', icon: 'rocket', tone: 'rose', label: t('menu.publishing'), hint: t('menu.publishingHint') },
        { id: 'connections', tab: 'connections', icon: 'link', tone: 'amber', label: t('menu.connections'), hint: t('menu.connectionsHint') },
        { id: 'domains', tab: 'custom-domain', icon: 'globe', tone: 'green', label: t('menu.domains'), hint: t('menu.domainsHint') },
        { id: 'builder', tab: 'builder', icon: 'tools', tone: 'slate', label: t('menu.builder'), hint: t('menu.builderHint') },
        { id: 'deploy', tab: 'deploy', icon: 'bolt', tone: 'slate', label: t('menu.deploy'), hint: t('menu.deployHint') },
        { id: 'dashboard', tab: 'dashboard', icon: 'chart', tone: 'slate', label: t('menu.dashboard'), hint: t('menu.dashboardHint') }
      ]
    },
    {
      id: 'settings', title: t('menu.groupSettings'),
      items: [
        { id: 'account', tab: 'account', icon: 'user', tone: 'blue', label: t('menu.profile'), hint: t('menu.profileHint') },
        { id: 'languages', toggle: 'languages', icon: 'language', tone: 'cyan', label: t('menu.languages'), hint: t('menu.languagesHint') },
        { id: 'logs', tab: 'logs', icon: 'logs', tone: 'slate', label: t('menu.logs'), hint: t('menu.logsHint') },
        { id: 'support', tab: 'support', icon: 'help', tone: 'blue', label: t('menu.support'), hint: t('menu.supportHint') },
        { id: 'share', command: 'share', icon: 'share', tone: 'green', label: t('menu.share'), hint: t('menu.shareHint') }
      ]
    }
  ];
}

function MenuRow({ item, active, onSelect, expanded }) {
  return (
    <button
      type="button"
      className={`side-menu-row${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      aria-expanded={item.toggle ? Boolean(expanded) : undefined}
      onClick={() => onSelect(item)}
    >
      <span className={`side-menu-icon tone-${item.tone || 'slate'}`}>{MENU_ICONS[item.icon]}</span>
      <span className="side-menu-text">
        <strong>{item.label}</strong>
        {item.hint && <small>{item.hint}</small>}
      </span>
      {(item.tab || item.toggle) && <span className={`side-menu-chevron${expanded ? ' is-open' : ''}`}>{MENU_ICONS.chevron}</span>}
    </button>
  );
}

export default function SideMenu({
  open, onClose, t, dir, tenant, credits, profileImage, profileInitial, profileImageError,
  onPickProfileImage, onRemoveProfileImage, activeTab, onNavigate, onChatCommand,
  preference, onSetLocale, connected, onLogout
}) {
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Move focus into the menu so keyboard and screen-reader users land in it.
    const focusTimer = setTimeout(() => closeRef.current?.focus(), 30);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(focusTimer); };
  }, [open, onClose]);

  const select = item => {
    if (item.toggle === 'languages') { setLanguagesOpen(value => !value); return; }
    if (item.command) { onChatCommand(item.command); return; }
    if (item.tab) onNavigate(item.tab);
  };

  const name = tenant?.name || tenant?.businessName || (tenant?.email ? tenant.email.split('@')[0] : '');

  return (
    <div className={`side-menu-layer${open ? ' is-open' : ''}`}>
      <button type="button" className="side-menu-backdrop" tabIndex={-1} aria-hidden="true" onClick={onClose} />
      <aside id="client-side-menu" className="side-menu" dir={dir} aria-label={t('app.menu')} aria-hidden={!open}>
        <div className="side-menu-top">
          <button ref={closeRef} type="button" className="side-menu-close" onClick={onClose} aria-label={t('app.closeMenu')}>{MENU_ICONS.close}</button>
          <span className="side-menu-brand" dir="ltr">Code<span>Voyage</span></span>
        </div>

        <div className="side-menu-scroll">
          <button type="button" className={`side-menu-card side-menu-account${activeTab === 'account' ? ' is-active' : ''}`} onClick={() => onNavigate('account')}>
            <span className="side-menu-avatar" aria-hidden="true">{profileImage ? <img src={profileImage} alt="" /> : profileInitial}</span>
            <span className="side-menu-text">
              <strong>{name}</strong>
              <small dir="ltr">{tenant?.email}</small>
            </span>
            <span className="side-menu-chevron">{MENU_ICONS.chevron}</span>
          </button>
          <div className="side-menu-photo-actions">
            <button type="button" onClick={onPickProfileImage}>{t('app.uploadProfileImage')}</button>
            {profileImage && <button type="button" onClick={onRemoveProfileImage}>{t('app.removeProfileImage')}</button>}
          </div>
          {profileImageError && <small className="side-menu-error" role="alert">{profileImageError}</small>}

          <button type="button" className="side-menu-card side-menu-credits" onClick={() => onNavigate('account')}>
            <span className="side-menu-icon tone-gold">{MENU_ICONS.coins}</span>
            <span className="side-menu-text"><strong>{t('menu.myCredits')}</strong><small>{t('menu.myCreditsHint')}</small></span>
            <span className="side-menu-credit-value">{credits ?? '—'}</span>
            <span className="side-menu-chevron">{MENU_ICONS.chevron}</span>
          </button>

          {activeTab !== 'chat' && (
            <button type="button" className="side-menu-card side-menu-back" onClick={() => onNavigate('chat')}>
              <span className="side-menu-icon tone-blue">{MENU_ICONS.chat}</span>
              <span className="side-menu-text"><strong>{t('menu.backToChat')}</strong><small>{t('menu.backToChatHint')}</small></span>
            </button>
          )}

          {menuGroups(t).map(group => (
            <section key={group.id} className={`side-menu-group group-${group.id}`} aria-label={group.title}>
              <h2>{group.icon && <span className="side-menu-group-icon">{MENU_ICONS[group.icon]}</span>}{group.title}</h2>
              {group.items.map(item => (
                <React.Fragment key={item.id}>
                  <MenuRow item={item} active={Boolean(item.tab) && item.tab === activeTab} expanded={item.toggle === 'languages' && languagesOpen} onSelect={select} />
                  {item.toggle === 'languages' && languagesOpen && (
                    <div className="side-menu-languages" role="group" aria-label={t('app.languages')}>
                      {[['auto', t('app.automatic'), t('app.automaticDescription')], ['he', t('app.hebrew'), 'עברית'], ['en', t('app.english'), 'English']].map(([value, label, note]) => (
                        <button key={value} type="button" className={preference === value ? 'selected' : ''} aria-pressed={preference === value} onClick={() => onSetLocale(value)}>
                          <span>{label}</span><small>{note}</small>
                        </button>
                      ))}
                      <p>{t('app.additionalLanguages')}: {t('app.additionalLanguagesNotice')}</p>
                      <div className="side-menu-more-languages">{['german', 'spanish', 'french', 'arabic', 'russian'].map(key => <em key={key}>{t(`app.${key}`)}</em>)}</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </section>
          ))}
        </div>

        <div className="side-menu-bottom">
          <span className={`side-menu-status${connected ? ' is-connected' : ''}`}><i aria-hidden="true" />{connected ? t('app.connected') : t('app.disconnected')}</span>
          <button type="button" className="side-menu-logout" onClick={onLogout}>
            <span className="side-menu-icon tone-red">{MENU_ICONS.logout}</span>
            <strong>{t('app.signOut')}</strong>
          </button>
        </div>
      </aside>
    </div>
  );
}
